import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { areaService } from '@/services/areaService';
import { areaTypeService } from '@/services/areaTypeService';
import { projectService } from '@/services/projectService';
import { useTheme } from '@/hooks/useTheme';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { useDebounce } from '@/hooks/useDebounce';
import { Area, AreaType } from '@/types';
import { formatDate } from '@/utils';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { Toast } from '@/components/Toast';

const areaSchema = z.object({
  name: z.string().min(1, 'Le nom est requis'),
  code: z.string().min(1, 'Le code est requis'),
  number: z.string().optional(),
  typologie: z.string().optional(),
  surface: z.string().optional(),
  orientation: z.string().optional(),
  occupancyStatus: z.string().optional(),
});

const createAreaSchema = z.object({
  name: z.string().min(1, 'Le nom est requis'),
  code: z.string().min(1, 'Le code est requis'),
  number: z.string().optional(),
  typologie: z.string().optional(),
  surface: z.string().optional(),
  orientation: z.string().optional(),
  occupancyStatus: z.string().optional(),
  type: z.string().min(1, 'Le type est requis'),
  parent: z.number().optional(),
});

type AreaFormData = z.infer<typeof areaSchema>;
type CreateAreaFormData = z.infer<typeof createAreaSchema>;

interface AreaTreeNode extends Area {
  children?: AreaTreeNode[];
  expanded?: boolean;
}

export function AreasManagementPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isDark, toggleTheme } = useTheme();
  const { user } = useAuthStore();
  const { logout } = useAuth();
  const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
  const [expandedAreas, setExpandedAreas] = useState<Set<number>>(new Set());
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createAsSubZone, setCreateAsSubZone] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const isInitialLoad = useRef(true);
  const isCreateInitialLoad = useRef(true);

  // Fetch project
  const {
    data: project,
    isLoading: projectLoading,
    isError: projectError,
  } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectService.getProject(Number(projectId)),
    enabled: !!projectId,
  });

  // Fetch areas with search (API-side filtering)
  const { data: areas = [], isLoading: areasLoading } = useQuery({
    queryKey: ['areas', projectId, debouncedSearchQuery, 'tree'],
    queryFn: () => areaService.getAdminAreasByProjectLight(Number(projectId), debouncedSearchQuery || undefined),
    enabled: !!projectId,
  });

  // Fetch selected area details
  const { data: selectedArea } = useQuery({
    queryKey: ['area', selectedAreaId],
    queryFn: () => areaService.getArea(selectedAreaId!),
    enabled: !!selectedAreaId,
  });

  // Fetch area types
  const { data: areaTypes = [] } = useQuery({
    queryKey: ['areaTypes'],
    queryFn: () => areaTypeService.getAreaTypes(),
  });

  // Build tree structure from areas (already filtered by API)
  const areaTree = useMemo(() => {
    if (!areas.length) return [];

    const areaMap = new Map<number, AreaTreeNode>();
    const rootAreas: AreaTreeNode[] = [];

    // Create map of all areas
    areas.forEach((area) => {
      areaMap.set(area.id, { ...area, children: [], expanded: expandedAreas.has(area.id) });
    });

    // Build tree
    areas.forEach((area) => {
      const node = areaMap.get(area.id)!;
      if (area.parent) {
        let parentId: number;
        if (typeof area.parent === 'object') {
           parentId = area.parent.id;
        } else if (typeof area.parent === 'string') {
           parentId = parseInt(area.parent.split('/').pop() || '0', 10);
        } else {
           parentId = area.parent as number;
        }

        const parent = areaMap.get(parentId);
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(node);
        } else {
          // Parent not in filtered results, show as root
          rootAreas.push(node);
        }
      } else {
        rootAreas.push(node);
      }
    });

    return rootAreas;
  }, [areas, expandedAreas]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    control,
  } = useForm<AreaFormData>({
    resolver: zodResolver(areaSchema),
    defaultValues: {
      name: '',
      code: '',
      number: '',
      typologie: '',
      surface: '',
      orientation: '',
      occupancyStatus: '',
    },
  });

  const {
    register: registerCreate,
    handleSubmit: handleSubmitCreate,
    formState: { errors: createErrors },
    reset: resetCreate,
    watch: watchCreate,
    setValue: setValueCreate,
    control: controlCreate,
  } = useForm<CreateAreaFormData>({
    resolver: zodResolver(createAreaSchema),
  });

  // Function to generate code from name
  const generateCodeFromName = (name: string): string => {
    return name
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '-') // Replace spaces with dashes
      .replace(/[^A-Z0-9-]/g, '') // Remove special characters except dashes
      .replace(/-+/g, '-') // Replace multiple dashes with single dash
      .replace(/^-|-$/g, ''); // Remove leading/trailing dashes
  };

  // Watch name field for edit form and auto-generate code
  const watchedName = watch('name');
  useEffect(() => {
    // Only generate code if it's not the initial load (user is typing)
    if (!isInitialLoad.current && watchedName && watchedName.trim()) {
      const generatedCode = generateCodeFromName(watchedName);
      setValue('code', generatedCode, { shouldValidate: false });
    }
  }, [watchedName, setValue]);

  // Watch name field for create form and auto-generate code
  const watchedCreateName = watchCreate('name');
  useEffect(() => {
    // Only generate code if modal is open and it's not the initial load
    if (isCreateModalOpen && !isCreateInitialLoad.current && watchedCreateName && watchedCreateName.trim()) {
      const generatedCode = generateCodeFromName(watchedCreateName);
      setValueCreate('code', generatedCode, { shouldValidate: false });
    }
  }, [watchedCreateName, setValueCreate, isCreateModalOpen]);

  // Reset form when selected area changes
  useEffect(() => {
    if (selectedArea) {
      isInitialLoad.current = true;
      const formData = {
        name: selectedArea.name || '',
        code: selectedArea.code || '',
        number: selectedArea.number || '',
        typologie: selectedArea.typologie || '',
        surface: selectedArea.surface || '',
        orientation: selectedArea.orientation || '',
        occupancyStatus: selectedArea.occupancyStatus || '',
      };
      reset(formData, { keepDefaultValues: false });
      // Ensure typologie and occupancyStatus are set correctly for the selects
      if (selectedArea.typologie) {
        setValue('typologie', selectedArea.typologie, { shouldValidate: false, shouldDirty: false });
      }
      if (selectedArea.occupancyStatus) {
        setValue('occupancyStatus', selectedArea.occupancyStatus, { shouldValidate: false, shouldDirty: false });
      }
      // Reset flag after a short delay to allow form to initialize
      setTimeout(() => {
        isInitialLoad.current = false;
      }, 100);
    } else {
      // Reset form when no area is selected
      reset({
        name: '',
        code: '',
        number: '',
        typologie: '',
        surface: '',
        orientation: '',
        occupancyStatus: '',
      });
    }
  }, [selectedArea, reset, setValue]);

  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [showToast, setShowToast] = useState(false);

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      console.log('Updating area with payload:', data);
      console.log('Area ID:', selectedAreaId);
      try {
        const result = await areaService.updateArea(selectedAreaId!, data);
        console.log('Update result:', result);
        return result;
      } catch (error) {
        console.error('Error in mutationFn:', error);
        throw error;
      }
    },
    onSuccess: async (data) => {
      console.log('Update success, data:', data);
      queryClient.invalidateQueries({ queryKey: ['areas', projectId] });
      queryClient.invalidateQueries({ queryKey: ['area', selectedAreaId] });
      setToastMessage('Zone modifiée avec succès !');
      setToastType('success');
      setShowToast(true);
      // Keep the edit panel open - don't reset selectedAreaId
      // Refresh the selected area data and update form
      if (selectedAreaId) {
        // Refetch the updated area to get latest data
        const updatedArea = await areaService.getArea(selectedAreaId);
        if (updatedArea) {
          reset({
            name: updatedArea.name || '',
            code: updatedArea.code || '',
            number: updatedArea.number || '',
            typologie: updatedArea.typologie || '',
            surface: updatedArea.surface || '',
            orientation: updatedArea.orientation || '',
            occupancyStatus: updatedArea.occupancyStatus || '',
          });
        }
      }
    },
    onError: (error: any) => {
      console.error('Error updating area:', error);
      console.error('Error response:', error?.response);
      console.error('Error data:', error?.response?.data);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.['hydra:description'] || error?.message || 'Erreur lors de la modification de la zone';
      setToastMessage(errorMessage);
      setToastType('error');
      setShowToast(true);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => areaService.deleteArea(selectedAreaId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['areas', projectId] });
      setSelectedAreaId(null);
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateAreaFormData) => {
      const payload: any = {
        name: data.name,
        code: data.code,
        project: `/api/projects/${projectId}`,
        type: `/api/area_types/${data.type}`,
      };
      if (data.number) payload.number = data.number;
      if (data.typologie) payload.typologie = data.typologie;
      if (data.surface) payload.surface = data.surface;
      if (data.orientation) payload.orientation = data.orientation;
      if (data.occupancyStatus) payload.occupancyStatus = data.occupancyStatus;
      if (data.parent) payload.parent = `/api/areas/${data.parent}`;
      return areaService.createArea(payload);
    },
    onSuccess: (newArea) => {
      queryClient.invalidateQueries({ queryKey: ['areas', projectId] });
      setToastMessage('Zone créée avec succès !');
      setToastType('success');
      setShowToast(true);
      setIsCreateModalOpen(false);
      resetCreate();
      setSelectedAreaId(newArea.id);
      // Expand parent if it's a sub-zone
      if (newArea.parent) {
        setExpandedAreas((prev) => new Set([...prev, newArea.parent!.id]));
      }
    },
    onError: () => {
      setToastMessage('Erreur lors de la création de la zone');
      setToastType('error');
      setShowToast(true);
    },
  });

  const onSubmit = (data: AreaFormData) => {
    if (selectedAreaId && selectedArea) {
      // Get type IRI - handle JSON-LD format with @id
      let typeIri: string;
      const type = selectedArea.type as any;
      
      if (!type) {
        console.error('Type is missing from selectedArea', selectedArea);
        return;
      }
      
      if (typeof type === 'string') {
        typeIri = type;
      } else if (type['@id']) {
        // JSON-LD format: use @id directly
        typeIri = type['@id'];
      } else if (type.id) {
        // Standard format: construct IRI from id
        typeIri = `/api/area_types/${type.id}`;
      } else {
        console.error('Unable to determine type IRI. Type object:', type);
        return;
      }

      // Get parent IRI if exists
      let parentIri: string | undefined;
      if (selectedArea.parent) {
        if (typeof selectedArea.parent === 'string') {
          parentIri = selectedArea.parent;
        } else if ((selectedArea.parent as any)?.['@id']) {
          parentIri = (selectedArea.parent as any)['@id'];
        } else if ((selectedArea.parent as any)?.id) {
          parentIri = `/api/areas/${(selectedArea.parent as any).id}`;
        }
      }

      const payload: any = {
        name: data.name,
        code: data.code,
        project: `/api/projects/${projectId}`,
        type: typeIri,
      };
      if (data.number) payload.number = data.number;
      if (data.typologie) payload.typologie = data.typologie;
      if (data.surface) payload.surface = data.surface;
      if (data.orientation) payload.orientation = data.orientation;
      if (data.occupancyStatus) payload.occupancyStatus = data.occupancyStatus;
      if (parentIri) payload.parent = parentIri;
      updateMutation.mutate(payload);
    }
  };

  const onSubmitCreate = (data: CreateAreaFormData) => {
    createMutation.mutate(data);
  };

  const handleDelete = () => {
    if (confirm('Êtes-vous sûr de vouloir supprimer cette zone ?')) {
      deleteMutation.mutate();
    }
  };

  const handleOpenCreateModal = (asSubZone: boolean = false) => {
    setCreateAsSubZone(asSubZone);
    setIsCreateModalOpen(true);
    isCreateInitialLoad.current = true;
    resetCreate({
      name: '',
      code: '',
      number: '',
      typologie: '',
      surface: '',
      orientation: '',
      type: '',
      parent: asSubZone && selectedAreaId ? selectedAreaId : undefined,
    });
    // Reset flag after a short delay to allow form to initialize
    setTimeout(() => {
      isCreateInitialLoad.current = false;
    }, 100);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    resetCreate();
  };

  const toggleExpand = (areaId: number) => {
    setExpandedAreas((prev) => {
      const next = new Set(prev);
      if (next.has(areaId)) {
        next.delete(areaId);
      } else {
        next.add(areaId);
      }
      return next;
    });
  };

  const renderAreaTree = (nodes: AreaTreeNode[], level = 0): JSX.Element[] => {
    return nodes.map((node) => (
      <div key={node.id}>
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
            selectedAreaId === node.id
              ? 'bg-primary/20 text-primary dark:bg-primary/30'
              : 'hover:bg-slate-100 dark:hover:bg-gray-800'
          }`}
          onClick={() => setSelectedAreaId(node.id)}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
        >
          {node.children && node.children.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(node.id);
              }}
              className="p-1 hover:bg-slate-200 dark:hover:bg-gray-700 rounded"
            >
              <span className="material-icons-outlined text-sm">
                {expandedAreas.has(node.id) ? 'expand_more' : 'chevron_right'}
              </span>
            </button>
          )}
          {!node.children || node.children.length === 0 ? (
            <span className="material-icons-outlined text-lg text-slate-500 dark:text-slate-400">
              {node.type.title === 'APPARTEMENT' ? 'home' : node.type.title === 'FLOOR' ? 'layers' : 'domain'}
            </span>
          ) : (
            <span className="material-icons-outlined text-lg text-slate-500 dark:text-slate-400">domain</span>
          )}
          <span className="flex-1 text-sm font-medium">{node.name}</span>
          {selectedAreaId === node.id && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Edit action
              }}
              className="p-1 hover:bg-slate-200 dark:hover:bg-gray-700 rounded"
            >
              <span className="material-icons-outlined text-sm">edit</span>
            </button>
          )}
        </div>
        {node.children && node.children.length > 0 && expandedAreas.has(node.id) && (
          <div>{renderAreaTree(node.children, level + 1)}</div>
        )}
      </div>
    ));
  };

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (!projectId) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <p className="text-red-500">Project ID manquant</p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="mt-4 text-primary hover:text-primary-600"
          >
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  if (projectLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-slate-500 dark:text-slate-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (projectError || !project) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <p className="text-red-500">Projet non trouvé</p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="mt-4 text-primary hover:text-primary-600"
          >
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      {/* Sidebar */}
      <AdminSidebar getUserInitials={getUserInitials} />

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        {/* Header */}
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="flex items-center gap-4">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              <span className="text-slate-400 dark:text-slate-600">Admin</span> / Zones
            </div>
            {project && (
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-slate-900 dark:text-white">{project.title}</span>
                {project.city && (
                  <>
                    <span className="text-slate-400">•</span>
                    <span className="text-sm text-slate-500 dark:text-slate-400">{project.city}</span>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors"
            >
              {isDark ? (
                <span className="material-icons-outlined">light_mode</span>
              ) : (
                <span className="material-icons-outlined">dark_mode</span>
              )}
            </button>
          </div>
        </header>

        {/* Content - Two Panels */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Structure */}
          <div className="w-80 flex-shrink-0 border-r border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A] flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-[#1E293B]">
              <div className="mb-4">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2 mb-3">
                  <span className="material-icons-outlined">account_tree</span>
                  Structure
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(`/admin/projects/${projectId}/areas/import`)}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                    title="Importer des zones"
                  >
                    <span className="material-icons-outlined text-base">upload</span>
                    <span>Importer</span>
                  </button>
                  <button
                    onClick={() => handleOpenCreateModal(false)}
                    className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-sky-600 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                    title="Ajouter une zone racine"
                  >
                    <span className="material-icons-outlined text-base">add</span>
                    <span>Ajouter</span>
                  </button>
                </div>
              </div>
              {/* Search Input */}
              <div className="relative">
                <span className="material-icons-outlined absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 text-sm">
                  search
                </span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher une zone..."
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                  >
                    <span className="material-icons-outlined text-sm">close</span>
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {areasLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : areaTree.length === 0 ? (
                <div className="text-center text-slate-500 dark:text-slate-400 py-8">
                  <p>Aucune zone trouvée</p>
                </div>
              ) : (
                <div className="space-y-1">{renderAreaTree(areaTree)}</div>
              )}
            </div>
          </div>

          {/* Right Panel - Details */}
          <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-[#0B1116]">
            {selectedArea ? (
              <div className="p-6">
                <div className="bg-white dark:bg-[#16202A] rounded-xl border border-slate-200 dark:border-[#1E293B] overflow-hidden">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-slate-200 dark:border-[#1E293B] flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900 dark:text-white">Détails de la zone</h2>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        Configuration de {selectedArea.name}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleDelete}
                        disabled={deleteMutation.isPending}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        <span className="material-icons-outlined text-sm">delete</span>
                        Supprimer
                      </button>
                      <button
                        onClick={handleSubmit(onSubmit)}
                        disabled={updateMutation.isPending}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        <span className="material-icons-outlined text-sm">save</span>
                        Enregistrer
                      </button>
                    </div>
                  </div>

                  {/* Form Content */}
                  <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
                    {/* Informations Générales */}
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 uppercase tracking-wide">
                        Informations Générales
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Nom de la zone
                          </label>
                          <input
                            {...register('name')}
                            className="w-full px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white"
                          />
                          {errors.name && (
                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name.message}</p>
                          )}
                        </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      <span className="material-icons-outlined text-sm">qr_code</span>
                      Code technique (ID) <span className="text-xs text-slate-500 dark:text-slate-400">(généré automatiquement)</span>
                    </label>
                    <input
                      {...register('code')}
                      readOnly
                      className="w-full px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-slate-50 dark:bg-[#0B1116]/50 text-slate-900 dark:text-white cursor-not-allowed"
                    />
                    {errors.code && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.code.message}</p>
                    )}
                  </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Numéro
                          </label>
                          <input
                            {...register('number')}
                            className="w-full px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Typologie
                          </label>
                          <Controller
                            name="typologie"
                            control={control}
                            render={({ field }) => {
                              // Find matching area type by code or title (case-insensitive, partial match)
                              const findMatchingType = (value: string | undefined) => {
                                if (!value) return null;
                                const normalizedValue = value.toUpperCase().trim();
                                return areaTypes.find((type) => {
                                  const typeCode = type.code?.toUpperCase().trim();
                                  const typeTitle = type.title?.toUpperCase().trim();
                                  return (
                                    typeCode === normalizedValue ||
                                    typeTitle === normalizedValue ||
                                    (typeCode && typeCode.startsWith(normalizedValue)) ||
                                    (typeTitle && typeTitle.includes(normalizedValue))
                                  );
                                });
                              };

                              const currentType = findMatchingType(field.value);
                              // Use code as value for matching, but display title
                              const selectValue = currentType ? (currentType.code || currentType.title) : field.value || '';

                              return (
                                <select
                                  {...field}
                                  key={`typologie-${selectedAreaId || 'new'}-${selectValue}`}
                                  value={selectValue}
                                  onChange={(e) => {
                                    const selectedType = areaTypes.find(
                                      (type) => type.code === e.target.value || type.title === e.target.value
                                    );
                                    // Store the code, not the title
                                    field.onChange(selectedType ? selectedType.code : e.target.value);
                                  }}
                                  className="w-full px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white"
                                >
                                  <option value="">Sélectionner...</option>
                                  {areaTypes.map((type) => (
                                    <option key={type.id} value={type.code || type.title}>
                                      {type.title}
                                    </option>
                                  ))}
                                  {/* Show current value if it doesn't match any area type */}
                                  {field.value &&
                                    field.value !== '' &&
                                    !currentType && (
                                      <option value={field.value}>{field.value}</option>
                                    )}
                                </select>
                              );
                            }}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Statut d'occupation
                          </label>
                          <Controller
                            name="occupancyStatus"
                            control={control}
                            render={({ field }) => (
                              <select
                                {...field}
                                key={`occupancyStatus-${selectedAreaId || 'new'}-${selectedArea?.occupancyStatus || ''}`}
                                value={field.value || ''}
                                className="w-full px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white"
                              >
                                <option value="">Sélectionner...</option>
                                <option value="Opérationnel">Opérationnel</option>
                                <option value="Vacant">Vacant</option>
                                <option value="En travaux">En travaux</option>
                              </select>
                            )}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Spécifications */}
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 uppercase tracking-wide">
                        Spécifications
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Surface (m²)
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              {...register('surface')}
                              type="number"
                              step="0.1"
                              className="flex-1 px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white"
                            />
                            <span className="text-slate-500 dark:text-slate-400">m²</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Orientation
                          </label>
                          <Controller
                            name="orientation"
                            control={control}
                            render={({ field }) => (
                              <div className="flex gap-2">
                                {['Nord', 'Sud', 'Est', 'Ouest'].map((dir) => (
                                  <button
                                    key={dir}
                                    type="button"
                                    className={`px-4 py-2 border rounded-lg transition-colors ${
                                      field.value === dir
                                        ? 'bg-primary text-white border-primary'
                                        : 'border-slate-300 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-800'
                                    }`}
                                    onClick={() => {
                                      // Toggle: if already selected, deselect; otherwise select
                                      field.onChange(field.value === dir ? '' : dir);
                                    }}
                                  >
                                    {dir}
                                  </button>
                                ))}
                              </div>
                            )}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Zone Parente */}
                    {selectedArea.parent && (
                      <div>
                        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 uppercase tracking-wide">
                          Zone Parente
                        </h3>
                        <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-[#0B1116] rounded-lg border border-slate-200 dark:border-[#1E293B]">
                          <span className="material-icons-outlined text-slate-500 dark:text-slate-400">
                            account_tree
                          </span>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-slate-900 dark:text-white">
                              {selectedArea.parent.name}
                            </p>
                            {selectedArea.parent.parent && (
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {selectedArea.parent.parent.name}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            className="px-3 py-1 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors"
                          >
                            Modifier
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="pt-4 border-t border-slate-200 dark:border-[#1E293B]">
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                        Dernière modification: {selectedArea.createdAt ? formatDate(selectedArea.createdAt) : 'N/A'} par{' '}
                        {user?.fullName || 'Admin'}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleOpenCreateModal(true)}
                        className="w-full px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-[#16202A] hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                      >
                        <span className="material-icons-outlined text-sm">add</span>
                        Ajouter une sous-zone
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-slate-500 dark:text-slate-400">
                  <span className="material-icons-outlined text-6xl mb-4">map</span>
                  <p>Sélectionnez une zone pour voir ses détails</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Create Area Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-[#16202A] rounded-xl border border-slate-200 dark:border-[#1E293B] w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-[#1E293B] flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                {createAsSubZone ? 'Ajouter une sous-zone' : 'Ajouter une zone racine'}
              </h2>
              <button
                onClick={handleCloseCreateModal}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-white"
              >
                <span className="material-icons-outlined">close</span>
              </button>
            </div>

            <form onSubmit={handleSubmitCreate(onSubmitCreate)} className="p-6 space-y-6">
              {/* Informations Générales */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 uppercase tracking-wide">
                  Informations Générales
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Nom de la zone <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...registerCreate('name')}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white"
                      placeholder="Ex: Bâtiment A"
                    />
                    {createErrors.name && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{createErrors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      <span className="material-icons-outlined text-sm">qr_code</span>
                      Code technique (ID) <span className="text-xs text-slate-500 dark:text-slate-400">(généré automatiquement)</span>
                    </label>
                    <input
                      {...registerCreate('code')}
                      readOnly
                      className="w-full px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-slate-50 dark:bg-[#0B1116]/50 text-slate-900 dark:text-white cursor-not-allowed"
                      placeholder="Sera généré automatiquement"
                    />
                    {createErrors.code && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{createErrors.code.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Type de zone <span className="text-red-500">*</span>
                    </label>
                    <select
                      {...registerCreate('type')}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white"
                    >
                      <option value="">Sélectionner un type...</option>
                      {areaTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.title}
                        </option>
                      ))}
                    </select>
                    {createErrors.type && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{createErrors.type.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Numéro
                    </label>
                    <input
                      {...registerCreate('number')}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white"
                      placeholder="Ex: COMMUNS-01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Typologie
                    </label>
                    <input
                      {...registerCreate('typologie')}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white"
                      placeholder="Ex: Parties Communes / Shared Space"
                    />
                  </div>
                </div>
              </div>

              {/* Spécifications */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 uppercase tracking-wide">
                  Spécifications
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Surface (m²)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        {...registerCreate('surface')}
                        type="number"
                        step="0.1"
                        className="flex-1 px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white"
                        placeholder="125.0"
                      />
                      <span className="text-slate-500 dark:text-slate-400">m²</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Orientation
                    </label>
                    <Controller
                      name="orientation"
                      control={controlCreate}
                      render={({ field }) => (
                        <div className="flex gap-2">
                          {['Nord', 'Sud', 'Est', 'Ouest'].map((dir) => (
                            <button
                              key={dir}
                              type="button"
                              className={`px-4 py-2 border rounded-lg transition-colors ${
                                field.value === dir
                                  ? 'bg-primary text-white border-primary'
                                  : 'border-slate-300 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-800'
                              }`}
                              onClick={() => {
                                // Toggle: if already selected, deselect; otherwise select
                                field.onChange(field.value === dir ? '' : dir);
                              }}
                            >
                              {dir}
                            </button>
                          ))}
                        </div>
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Zone Parente (si sous-zone) */}
              {createAsSubZone && selectedArea && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 uppercase tracking-wide">
                    Zone Parente
                  </h3>
                  <div className="p-4 bg-slate-50 dark:bg-[#0B1116] rounded-lg border border-slate-200 dark:border-[#1E293B]">
                    <p className="text-sm font-medium text-slate-900 dark:text-white">{selectedArea.name}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{selectedArea.code}</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-[#1E293B]">
                <button
                  type="button"
                  onClick={handleCloseCreateModal}
                  className="px-6 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-[#16202A] hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {createMutation.isPending ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Création...
                    </>
                  ) : (
                    <>
                      <span className="material-icons-outlined text-sm">add</span>
                      Créer la zone
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
}
